import { assign, createMachine, pure, sendParent, sendTo, spawn } from 'xstate';
import { PAGE_FUNCTION, SUB_FUNCTION } from '../../const/analytics';
import { ONE_TIME_PAYMENT_MACHINE_STATES_NAMES } from '../../const/machines';
import { isSecuredInstallmentLoanProduct } from '../../helpers/productTypeHelpers';
import { getHasMatchingPayment } from '../../ui/molecules/PaymentModalStates/helpers';
import { formMachine, getValues } from '../../xform';
import paymentMachine from '../payment/paymentMachine';
import { savePaymentResult, saveShouldShowOneClickAutopay, submitFailureAnalyticsHandler, submitReviewAnalyticsHandler, submitSuccessAnalyticsHandler } from './helpers';
import paymentAmountConfig from './paymentAmountConfig';
import paymentDateConfig from './paymentDateConfig';
import paymentMethodConfig from './paymentMethodConfig';
import paymentOptionConfig from './paymentOptionConfig';
export default createMachine({
    predictableActionArguments: true,
    id: 'oneTimeMachine',
    initial: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.INITIAL,
    schema: {
        events: {}
    },
    context: {
        apiMachineRef: undefined,
        errorType: undefined,
        featureFlags: undefined,
        form: undefined,
        shouldEnrollInOneClickAutopay: false,
        shouldShowOneClickAutopay: false,
        triggerAnalyticsEvent: () => undefined
    },
    states: {
        edit: {
            entry: [
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
                assign({
                    form({ apiMachineRef, form }) {
                        if (form) {
                            return form;
                        }
                        return spawn(formMachine.withContext({
                            fieldConfigs: [
                                paymentAmountConfig(apiMachineRef),
                                paymentDateConfig(apiMachineRef),
                                paymentMethodConfig(apiMachineRef),
                                paymentOptionConfig(apiMachineRef)
                            ]
                        }), {
                            name: 'oneTimePaymentForm'
                        });
                    }
                })
            ],
            on: {
                SET_NEW_PAYMENT_METHOD: {
                    actions: [
                        sendTo(({ apiMachineRef }) => apiMachineRef, {
                            type: 'FETCH_PAYMENT_METHODS'
                        }),
                        'sendToPaymentMethodField'
                    ]
                },
                NEXT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.REVIEW,
                    cond: 'hasNoMatchingPendingPayment',
                    actions: [
                        'saveShouldShowOneClickAutopay',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: submitReviewAnalyticsHandler
                        }
                    ]
                }
            }
        },
        review: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            on: {
                EDIT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.EDIT,
                    actions: {
                        type: 'triggerAnalyticsEvent',
                        eventOptions: (context) => {
                            const { form } = context;
                            const oneTimePaymentForm = getValues(form.getSnapshot().context.actors);
                            return {
                                message: oneTimePaymentForm.paymentOption,
                                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                                pageName: 'edit',
                                pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT
                            };
                        }
                    }
                },
                SUBMIT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.SUBMITTING
                },
                ONE_CLICK_AUTOPAY_ENROLLMENT: {
                    actions: 'updateOneClickAutopayEnrollment'
                }
            }
        },
        submitting: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            invoke: {
                id: 'payment',
                src: paymentMachine,
                data: ({ apiMachineRef, featureFlags, form, shouldEnrollInOneClickAutopay }) => {
                    const context = {
                        apiMachineRef,
                        featureFlags,
                        oneTimePaymentFormMachineRef: form,
                        shouldEnrollInOneClickAutopay
                    };
                    return context;
                },
                onDone: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.SUCCESS,
                    actions: [
                        'savePaymentResult',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: submitSuccessAnalyticsHandler
                        }
                    ]
                },
                onError: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.ERROR,
                    actions: [
                        'savePaymentResult',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: submitFailureAnalyticsHandler
                        },
                        sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' })
                    ]
                }
            }
        },
        success: {
            entry: [
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
                sendParent({ type: 'INCREMENT_CONVERSION' }),
                /*
                 * Secured Installment uses PaymentEligibility instead of AccountActivity to prevent further Payment
                 * so we need to force fetch eligibility
                 */
                sendTo(({ apiMachineRef }) => apiMachineRef, {
                    type: 'FETCH_PAYMENT_ELIGIBILITY',
                    force: true
                }),
                /*
                 * We need to force fetch account activities so that this payment transaction
                 * would show up in MMA. We also need it to be accurately reflected in `make-a-payment`
                 * because it's used to prevent a user from making a payment with the same
                 * payment method/date combo
                 */
                'shouldInvokeAccountActivity'
            ]
        },
        error: {},
        cancelled: {
            type: 'final',
            entry: sendParent({ type: 'CANCEL' })
        }
    },
    on: {
        CANCEL: {
            target: 'cancelled',
            actions: {
                type: 'triggerAnalyticsEvent',
                eventOptions: () => {
                    return {
                        pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                        pageName: 'cancel',
                        pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT
                    };
                }
            }
        }
    }
}, {
    actions: {
        triggerAnalyticsEvent: (context, event, meta) => {
            const { triggerAnalyticsEvent } = context;
            triggerAnalyticsEvent(meta.action.eventOptions(context));
        },
        shouldInvokeAccountActivity: pure((context) => {
            const { accountSummaryQueryResult } = context.apiMachineRef.getSnapshot().context;
            const productType = accountSummaryQueryResult.data.product.type;
            if (isSecuredInstallmentLoanProduct(productType)) {
                return sendTo(context.apiMachineRef, {
                    type: 'INVALIDATE_ACCOUNT_ACTIVITY'
                });
            }
            return sendTo(context.apiMachineRef, {
                type: 'FETCH_ACCOUNT_ACTIVITY',
                force: true
            });
        }),
        saveShouldShowOneClickAutopay,
        savePaymentResult,
        updateOneClickAutopayEnrollment: assign({
            shouldEnrollInOneClickAutopay: (context, { enrollment }) => {
                return enrollment;
            }
        }),
        sendToPaymentMethodField: sendTo(({ form }) => {
            return form.getSnapshot().context.actors.paymentMethod;
        }, (_, { paymentMethodId }) => {
            return {
                type: 'CHANGE',
                value: paymentMethodId
            };
        })
    },
    guards: {
        hasNoMatchingPendingPayment: ({ apiMachineRef, form }) => {
            const formMachineSnapshot = form.getSnapshot();
            const { product } = apiMachineRef.getSnapshot().context.accountSummaryQueryResult.data;
            if (isSecuredInstallmentLoanProduct(product.type))
                return true;
            const { activity } = apiMachineRef.getSnapshot().context.accountActivityQueryResult.data;
            const { paymentDate, paymentMethod } = getValues(formMachineSnapshot.context.actors);
            const hasMatchingPayment = getHasMatchingPayment(paymentDate, paymentMethod, activity);
            return !hasMatchingPayment;
        }
    }
});
